import { useLayoutEffect, useState } from "react";

/**
 * Returns the current window dimensions [width, height], updating on resize.
 * Debounces resize events by 100ms.
 *
 * Starts as [undefined, undefined] so the server and the first client render
 * agree, then measures once on mount. Without that first measurement callers
 * that only react to a change (e.g. the small-screen notification) would never
 * fire, since a phone never emits a resize event.
 */
const useWindowResize = (): [number | undefined, number | undefined] => {
  const [windowSize, setWindowSize] = useState<
    [number | undefined, number | undefined]
  >([undefined, undefined]);

  useLayoutEffect(() => {
    setWindowSize([window.innerWidth, window.innerHeight]);

    let timer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setWindowSize([window.innerWidth, window.innerHeight]);
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return windowSize;
};

export default useWindowResize;
